/**
 * MED-1266 — Frontend twin of the backend canonical attendance definition.
 *
 * The backend computes the pooled attendance rate and its marking coverage; this module
 * turns those raw numbers into the display rules every surface must share, so no surface
 * invents its own treatment:
 *
 *   - Unknown vs 0% (AC13): zero recorded outcomes is NOT 0%, it is un-recorded.
 *   - Incomplete coverage (AC10): when some of the population is still unmarked, the rate
 *     carries a visible signal WITHOUT hover, plus a "<marked> of <population> marked" detail.
 *   - Complete coverage (AC11): nothing extra is shown.
 *
 * No attendance rate is computed here — callers pass the server's canonical figures.
 */

export interface AttendanceCoverageInput {
  /** Canonical pooled rate as integer percent, or null when unknown (no recorded outcomes). */
  rate: number | null;
  /** Rows with a recorded outcome — the coverage numerator (= the rate's denominator). */
  marked: number | null | undefined;
  /** In-scope population the rate covers — the coverage denominator (marked + pending). */
  population: number | null | undefined;
}

export interface AttendanceCoverageDisplay {
  /** True when there are zero recorded outcomes → show an un-recorded state, not a percent. */
  isUnrecorded: boolean;
  /** True once every in-scope member has a recorded outcome → show no marker/annotation. */
  isComplete: boolean;
  /** Rate as a percent string, or an em-dash placeholder when un-recorded. */
  rateLabel: string;
  /** Short coverage annotation, e.g. "5 of 6 marked". Empty when complete or un-recorded. */
  coverageLabel: string;
  /** Longer detail for a tooltip, e.g. "Based on 5 of 6 recorded". Empty when complete. */
  detailLabel: string;
}

export const UNRECORDED_LABEL = 'Not recorded';

/** Turn the server's canonical rate + coverage into shared display rules. */
export function getAttendanceCoverageDisplay(input: AttendanceCoverageInput): AttendanceCoverageDisplay {
  const marked = Number(input.marked) || 0;
  const population = Number(input.population) || 0;
  const isUnrecorded = marked <= 0 || input.rate === null || input.rate === undefined;
  const isComplete = !isUnrecorded && population > 0 && marked >= population;

  return {
    isUnrecorded,
    isComplete,
    rateLabel: isUnrecorded ? '—' : `${input.rate}%`,
    coverageLabel: isUnrecorded || isComplete ? '' : `${marked} of ${population} marked`,
    detailLabel: isUnrecorded ? '' : `Based on ${marked} of ${population} recorded`,
  };
}
